import { useSize } from 'common/utils/hooks/useSize';
import { Flex } from 'component-library/components/layout/Flex';
import { Body } from 'component-library/components/typography/Body';
import { Subheading } from 'component-library/components/typography/Subheading';
import { colors } from 'component-library/styles/colors';
import { formatNumberAsCurrency } from 'helpers/formatNumberAsCurrency';
import React, { FC, useState } from 'react';
import styled from 'styled-components';

import {
  averagePaycheck,
  PaycheckChartData,
  sumOfAllPaychecks,
} from './paycheckCalculatorUtils';
import { Bar, ComposedChart, Line, XAxis, YAxis } from 'recharts';

interface PaycheckChartProps {
  paycheckData: PaycheckChartData[];
  retirementSavings: number;
}

const Legend = styled.div<{ color: string }>`
  width: 50px;
  height: 16px;
  margin: 0 10px;
  background-color: ${({ color }) => color};
`;

const StyledFlex = styled(Flex)`
  width: 100%;

  ${Flex} {
    flex: 1;
  }
`;

const StyledSubheading = styled(Subheading)`
  display: flex;
  align-items: baseline;
`;

export const PaycheckChart: FC<PaycheckChartProps> = ({
  paycheckData,
  retirementSavings,
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerSize = useSize(container);

  const xAxisTicks = paycheckData
    .map((pd) => pd.age)
    .filter((age) => age % 10 === 0);

  const totalPaycheck = sumOfAllPaychecks(paycheckData);
  const growthFactor = (totalPaycheck / retirementSavings).toFixed(1);
  const highestPaycheck = Math.max(...paycheckData.map((pd) => pd.paycheck));

  return (
    <div ref={setContainer}>
      <Flex alignItems="center" marginBottom={32} column>
        <Subheading variant={1} marginBottom={16}>
          Your monthly paycheck estimate
        </Subheading>
        <StyledFlex marginBottom={64}>
          <Flex column>
            <Subheading variant={2} medium>
              Average paycheck
            </Subheading>
            <StyledSubheading variant={2}>
              {formatNumberAsCurrency(averagePaycheck(paycheckData))}
              <Body marginLeft={4} variant={4}>
                /month
              </Body>
            </StyledSubheading>
          </Flex>
          <Flex column>
            <Subheading variant={2} medium>
              Lifetime payouts
            </Subheading>
            <StyledSubheading variant={2}>
              {formatNumberAsCurrency(sumOfAllPaychecks(paycheckData))}
              <Body variant={4} marginLeft={4}>
                (~{growthFactor}x growth)
              </Body>
            </StyledSubheading>
          </Flex>
        </StyledFlex>
      </Flex>
      <ComposedChart
        width={containerSize?.width || 200}
        height={275}
        data={paycheckData}
      >
        <XAxis
          axisLine={false}
          dataKey="age"
          tickLine={false}
          tick={{ fontSize: 12, color: colors.black, fontWeight: 300 }}
          ticks={xAxisTicks}
        />
        <YAxis yAxisId="balance" hide />
        <YAxis
          yAxisId="paycheck"
          orientation="right"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, color: colors.black, fontWeight: 300 }}
          tickFormatter={(val: number) => formatNumberAsCurrency(val)}
          ticks={[highestPaycheck]}
        />
        <Bar dataKey="paycheck" fill={colors.green} yAxisId="paycheck" />
        <Line
          dataKey="balance"
          yAxisId="balance"
          strokeWidth={4}
          dot={{ stroke: colors.navyBlue, fill: colors.navyBlue }}
          stroke={colors.navyBlue}
        />
      </ComposedChart>
      <Flex justifyContent="center" alignItems="center">
        <Legend color={colors.green} />
        <Body as="div" variant={1}>
          Monthly paycheck
        </Body>
        <Legend color={colors.navyBlue} />
        <Body as="div" variant={1}>
          Balance
        </Body>
      </Flex>
    </div>
  );
};
